import type { StackProps, SystemStyleObject } from '@hover/blueprint';
import { HStack, forwardRef, transition } from '@hover/blueprint';
import { mergeDeepRight } from 'ramda';
import React from 'react';

import { isOriginalImageDisplay } from 'src/modules/homeownerApp/helpers';
import type { AiDisplayImage } from 'src/shared/graphql/generated';

import { glass, withHover } from '../../helpers/styles';
import { CustomizeButton } from '../CustomizeButton';
import { SaveButton } from '../SaveButton';
import { ShareButton } from '../ShareButton';

export interface ThumbnailEngagementButtonsProps extends StackProps {
  image: AiDisplayImage;
}

const ThumbnailEngagementButtons = forwardRef<
  ThumbnailEngagementButtonsProps,
  'div'
>(({ image, ...props }, ref) => {
  // We need a deep merge here because both the glass
  // styles and these local styles define hover states
  const styles = mergeDeepRight(glass('dark'), {
    opacity: 0,
    ...withHover({ _groupHover: { opacity: 1 } }),
    _groupFocusWithin: { opacity: 1 },
    ...transition.easing250('background', 'opacity'),
  }) satisfies SystemStyleObject;

  return (
    <HStack
      bottom="400"
      display={{ base: 'none', desktop: 'flex' }}
      justifyContent="space-between"
      left="400"
      pointerEvents="none"
      position="absolute"
      right="400"
      {...props}
      ref={ref}
    >
      <CustomizeButton
        {...transition.easing250('opacity')}
        _focus={{ opacity: 1 }}
        image={image}
        pointerEvents="auto"
        sx={styles}
        trackingData={{
          track_id: 'customize-grid-view',
          view_type: 'grid-view',
        }}
      />
      {!isOriginalImageDisplay(image) && (
        <HStack
          _focusWithin={{ opacity: 1 }}
          borderRadius="round"
          cursor="pointer"
          justifyContent="end"
          opacity={1}
          pointerEvents="auto"
          spacing="200"
        >
          <ShareButton
            aiDisplayImage={image}
            {...transition.easing250('opacity')}
            sx={styles}
          />
          <SaveButton colorScheme="light" image={image} sx={styles} />
        </HStack>
      )}
    </HStack>
  );
});

export { ThumbnailEngagementButtons };
